/**
 * The two machine codes the post-cancellation lifecycle rejects with (PREC-3900).
 *
 * `PLAN_INACTIVE` — a cancelled company tried to send a message, spend credits or start a
 * campaign during its read-only grace month. `COMPANY_ARCHIVED` — an archived company touched a
 * route the wall does not allow.
 *
 * Both arrive as a **403**, and both are lifecycle states rather than session failures: the user
 * is signed in, their token is fine, and there is a screen or a toast that explains the state. So
 * neither may be treated the way a permissions 403 is — see `rejectRedirect.ts`, and the PREC-3876
 * incident it documents, where any rejected 403 reloaded the whole SPA.
 */
export const PLAN_INACTIVE_CODE = "PLAN_INACTIVE";
export const COMPANY_ARCHIVED_CODE = "COMPANY_ARCHIVED";

const LIFECYCLE_CODES: string[] = [PLAN_INACTIVE_CODE, COMPANY_ARCHIVED_CODE];

/**
 * The machine code out of a rejected RTK Query error, or `null`.
 *
 * The API's `HttpException` body is `{ statusCode, message, error }` with the code repeated in
 * `message` and `error` (see `PlanInactiveException`), but Nest's default shape puts a human
 * string in `error` — so both fields are read, and `message` may also be an array of validation
 * strings, which is why only string values count.
 */
export const readApiErrorCode = (error: unknown): string | null => {
	const data = (error as { data?: unknown })?.data;

	if (!data || typeof data !== "object") {
		return null;
	}

	const { message, error: errorField } = data as { message?: unknown; error?: unknown };

	if (typeof message === "string" && message) {
		return message;
	}

	return typeof errorField === "string" && errorField ? errorField : null;
};

/** Whether a rejected request failed because the company's plan is no longer effective. */
export const isPlanInactiveError = (error: unknown): boolean =>
	readApiErrorCode(error) === PLAN_INACTIVE_CODE;

/** Whether a rejected request failed on either lifecycle state. */
export const isLifecycleError = (error: unknown): boolean => {
	const code = readApiErrorCode(error);

	return code !== null && LIFECYCLE_CODES.includes(code);
};
